import { motion } from 'framer-motion';
// material
import { alpha, styled } from '@mui/material/styles';
import { Typography, Container, Grid, Stack, Box } from '@mui/material';
import { MotionInView, varFadeInUp, varWrapEnter } from '../../animate';
import { SxProps, useTheme } from '@mui/system';
import { CSSProperties } from 'react';

// ----------------------------------------------------------------------

const RootStyle = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  zIndex: 8,
  top: 0,
  display: 'block',
  height: '100%'
}));

const ContentStyle = styled(motion.div)(({ theme }) => ({
  zIndex: 10,
  display: 'block',
  width: '100%',
  position: 'relative',
  top: 0
}));

// ----------------------------------------------------------------------

export interface LandingPageHeroBlackProps {
  title?: string;
  image?: string;
  overlay?: string;
  cards: CardProps[];
  boxBlurStyle?: CSSProperties;
  titleStyle?: CSSProperties;
  sx?: SxProps;
}
const BoxBlur = styled(motion.div)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  minHeight: 160,
  color: theme.palette.common.white,
  padding: theme.spacing(3),
  width: '90%'
}));

interface CardProps {
  name: string;
  icon: string;
  rate: number;
  comment: string;
}

export default function LandingPageHeroBlack({
  title,
  image,
  cards,
  boxBlurStyle,
  titleStyle,
  sx
}: LandingPageHeroBlackProps) {
  const theme = useTheme();
  return (
    <>
      <RootStyle
        sx={{
          ...sx,
          ...{
            backgroundColor: image
              ? (theme) => alpha(theme.palette.common.black, 0.9)
              : theme.palette.common.white,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url(${image})`,
            backgroundPositionX: '63%'
          }
        }}
        initial="initial"
        animate="animate"
        variants={varWrapEnter}
      >
        <ContentStyle variants={varFadeInUp}>
          <MotionInView variants={varFadeInUp}>
            <Container maxWidth={'lg'}>
              <Stack spacing={10}>
                <Box sx={{ margin: '1em 0' }} />
                <>
                  <Typography
                    variant={'h2'}
                    sx={{
                      textAlign: 'center',
                      color: theme.palette.common.white,
                      display: {
                        xl: 'block',
                        lg: 'block',
                        md: 'block',
                        sm: 'none',
                        xs: 'none'
                      },
                      ...titleStyle
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant={'h4'}
                    sx={{
                      textAlign: 'center',
                      color: theme.palette.common.white,
                      display: {
                        xl: 'none',
                        lg: 'none',
                        md: 'none',
                        sm: 'block',
                        xs: 'block'
                      },
                      ...titleStyle
                    }}
                  >
                    {title}
                  </Typography>
                </>

                <Grid container spacing={2}>
                  {cards.map((card, index) => (
                    <Grid key={index} item sm={12} xs={12} md={4} lg={4} xl={4}>
                      <MotionInView variants={varFadeInUp}>
                        <BoxBlur sx={boxBlurStyle}>
                          <Stack spacing={2}>
                            <Typography variant={'subtitle1'}>{card.name}</Typography>
                            <Stack direction={'row'}>
                              {[...Array(card.rate)].map((_, index) => (
                                <img width={16} key={index} src={card.icon} alt={card.icon} />
                              ))}
                            </Stack>
                            <Typography variant={'body2'}>{card.comment}</Typography>
                          </Stack>
                        </BoxBlur>
                      </MotionInView>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ margin: '2em 0' }} />
              </Stack>
            </Container>
          </MotionInView>
        </ContentStyle>
      </RootStyle>
    </>
  );
}
